import { DraftWaypointStatus } from '@caresend/types';
import { isDraftWaypointDenormalized, hasStatus, capitalize } from '@caresend/utils';
import { defineComponent, ref, computed, watch } from 'vue';
import __vue_component__$1 from '../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$2 from '../../../../layout/InfoSubtitle.vue.js';
import __vue_component__$3 from '../../../../layout/InfoTitle.vue.js';
import { getWaypointCardTitle, getWaypointCardSubtitle, isSchedulingFilterReady } from './helpers.js';
import __vue_component__$4 from '../../../../symbols/StatusIndicator/StatusIndicator.vue.js';
import __vue_component__$5 from '../../../../ui/Tag/Tag.vue.js';
import { getWaypointCardStatusInfo } from '../../../../../functions/itinerary/waypoints.js';
import { draftWaypointDetailsRoute } from '../../../../../router/routes/locations.js';
import { schedulingFlowRoute } from '../../../../../router/routes/schedulingFlow/locations.js';
import { isPatientUser, isLabOfficeUser } from '../../../../../store/helpers.js';
import { getLocalFacilityDetailsForSchedulingOrProVisitList } from '../../../../../store/modules/schedulingFlow/helpers/local.js';
import { getIsDebugMode } from '../../../../../store/modules/variables/helpers.js';
import { useDraftWaypointsStore } from '../../../../../store/pinia/draftWaypoints/index.js';
import { useOrdersStore } from '../../../../../store/pinia/orders/index.js';

var script = defineComponent({
    /** We should merge this with OrdersProcedureCard */
    name: 'OrderWaypointCard',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        InfoSubtitle: __vue_component__$2,
        InfoTitle: __vue_component__$3,
        StatusIndicator: __vue_component__$4,
        Tag: __vue_component__$5,
    },
    props: {
        /**
         * `UnencryptedDraftWaypoint` only passed in case of just-created order in pro. Cards
         * will show basic info such as 'Visit' and
         * 'Scheduling user will provide the address'. More specific info will be
         * shown once algolia data is updated.
         */
        draftWaypoint: {
            type: Object,
            required: true,
        },
        /** Used only in pro */
        orderID: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const draftWaypointStore = useDraftWaypointsStore();
        const ordersStore = useOrdersStore();
        const { getters } = draftWaypointStore;
        const isPatientApp = isPatientUser();
        const isSchedulingFlowLoading = ref(false);
        const isNewlyCreatedOrder = computed(() => !isDraftWaypointDenormalized(props.draftWaypoint));
        /**
         * We use this to determine if the scheduling flow is ready for newly
         * created orders.
         */
        const hasSchedulingFilters = ref(false);
        /**
         * When the denormalized draft waypoint is not available, the visit details
         * page is not available.
         */
        const disabledDetailsMessage = computed(() => isNewlyCreatedOrder.value
            ? 'Your order is processing. Details will be available shortly.'
            : undefined);
        /** Used when scheduling is not available */
        const disabledSchedulingMessage = computed(() => !hasSchedulingFilters.value
            ? 'Your order is processing. Scheduling will be available shortly.'
            : undefined);
        const facilityDetails = computed(() => isDraftWaypointDenormalized(props.draftWaypoint)
            ? getters.getFacilityDetailsOnDraftWaypoint(props.draftWaypoint.id)
            : getLocalFacilityDetailsForSchedulingOrProVisitList(props.draftWaypoint));
        const showDebugLocalTag = computed(() => {
            var _a;
            if (!getIsDebugMode() || !isDraftWaypointDenormalized(props.draftWaypoint)) {
                return false;
            }
            return !!((_a = props.draftWaypoint) === null || _a === void 0 ? void 0 : _a.isLocalData);
        });
        const denormalizedOrder = computed(() => props.orderID ? ordersStore.getters.getDenormalizedOrderByID(props.orderID) : undefined);
        const order = computed(() => props.orderID ? ordersStore.getters.getOrder(props.orderID) : undefined);
        const offlineScheduling = computed(() => {
            var _a, _b, _c, _d;
            return (_d = (_b = (_a = denormalizedOrder.value) === null || _a === void 0 ? void 0 : _a.offlineScheduling) !== null && _b !== void 0 ? _b : (_c = order.value) === null || _c === void 0 ? void 0 : _c.offlineScheduling) !== null && _d !== void 0 ? _d : false;
        });
        const title = computed(() => {
            var _a;
            return getWaypointCardTitle(props.draftWaypoint, offlineScheduling.value, (_a = facilityDetails.value) === null || _a === void 0 ? void 0 : _a.name);
        });
        const subtitle = computed(() => {
            var _a;
            if (!props.orderID)
                return '';
            return getWaypointCardSubtitle(props.draftWaypoint, getters.getIsCurrentUserSchedulingUser(props.draftWaypoint.id), offlineScheduling.value, facilityDetails.value, (_a = getters.getSchedulingUserName(props.draftWaypoint.id)) !== null && _a !== void 0 ? _a : undefined);
        });
        const statusInfo = computed(() => getWaypointCardStatusInfo(props.draftWaypoint, offlineScheduling.value));
        const labOrderIDs = computed(() => { var _a; return Object.values((_a = getters.getLabOrderIDsProcedureID(props.draftWaypoint.id)) !== null && _a !== void 0 ? _a : {}); });
        const labOrderIDsTagText = computed(() => {
            if (!labOrderIDs.value || !labOrderIDs.value.length)
                return undefined;
            return labOrderIDs.value.length === 1
                ? `Order ${labOrderIDs.value[0]}`
                : `Orders ${labOrderIDs.value.join(', ')}`;
        });
        const draftWaypointDetailsLocation = computed(() => draftWaypointDetailsRoute({
            params: {
                draftWaypointID: props.draftWaypoint.id,
            },
        }));
        const displayScheduleButton = computed(() => !offlineScheduling.value
            && hasStatus(props.draftWaypoint, DraftWaypointStatus.PENDING));
        watch(() => props.draftWaypoint, async (newDraftWaypoint) => {
            if (!isNewlyCreatedOrder.value) {
                hasSchedulingFilters.value = true;
                return;
            }
            hasSchedulingFilters.value = await isSchedulingFilterReady(newDraftWaypoint);
        }, { immediate: true });
        return {
            capitalize,
            disabledDetailsMessage,
            disabledSchedulingMessage,
            displayScheduleButton,
            draftWaypointDetailsLocation,
            isLabOfficeUser,
            isPatientApp,
            isSchedulingFlowLoading,
            labOrderIDsTagText,
            schedulingFlowRoute,
            showDebugLocalTag,
            statusInfo,
            subtitle,
            title,
        };
    },
});

export { script as default };
